import { env } from '@/config/env';
import { ApiResponse } from '@/types/api';

export class ApiError extends Error {
  public readonly correlationId: string;
  public readonly status: number;
  public readonly errorCode?: string;

  constructor(message: string, status: number, correlationId: string, errorCode?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.correlationId = correlationId;
    this.errorCode = errorCode;
  }
}

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'corr-' + Math.random().toString(36).substring(2, 15);
}

export async function fetchFromAdminApi<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null
): Promise<ApiResponse<T>> {
  const correlationId = options.headers && (options.headers as Record<string, string>)['X-Correlation-ID']
    ? (options.headers as Record<string, string>)['X-Correlation-ID']
    : generateCorrelationId();

  const url = `${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-ID': correlationId,
    ...(options.headers as Record<string, string>),
  };

  let resolvedToken = token;
  let devActor: string | null = null;
  let devPermissions: string[] | null = null;

  if (typeof window !== 'undefined') {
    if (!resolvedToken) {
      resolvedToken = localStorage.getItem('imarket_admin_token');
    }
    try {
      const storedUser = localStorage.getItem('imarket_admin_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed.email) devActor = parsed.email;
        if (Array.isArray(parsed.permissions)) devPermissions = parsed.permissions;
      }
    } catch {
      // ignore parsing error
    }
  }

  if (resolvedToken && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${resolvedToken}`;
  }

  if (devActor && !headers['X-Admin-Actor']) {
    headers['X-Admin-Actor'] = devActor;
  }

  if (devPermissions && devPermissions.length > 0 && !headers['X-Admin-Permissions']) {
    headers['X-Admin-Permissions'] = devPermissions.join(',');
  }

  try {
    const res = await fetch(url, {
      ...options,
      headers,
    });

    const responseCorrelationId = res.headers.get('X-Correlation-ID') || correlationId;

    let payload: ApiResponse<T>;
    try {
      payload = await res.json();
    } catch {
      if (!res.ok) {
        throw new ApiError(
          `Request failed with status ${res.status}: ${res.statusText}`,
          res.status,
          responseCorrelationId
        );
      }
      payload = {
        success: true,
        correlationId: responseCorrelationId,
        timestamp: new Date().toISOString(),
      };
    }

    if (!res.ok || !payload.success) {
      throw new ApiError(
        payload.error || `HTTP error ${res.status}`,
        res.status,
        payload.correlationId || responseCorrelationId,
        payload.errorCode
      );
    }

    return payload;
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      throw err;
    }
    const rawMessage = err instanceof Error ? err.message : 'Network error or service unreachable';
    if (typeof console !== 'undefined' && console.error) {
      console.error(`[AdminApi Error] ${url}:`, err);
    }
    throw new ApiError('Unable to connect to Admin API', 0, correlationId, 'NETWORK_UNAVAILABLE');
  }
}

